import { Room } from "colyseus.js";

/**
 * WebRTC peer-mesh voice for One Heart.
 *
 *  - Each pair of players negotiates a direct RTCPeerConnection.
 *  - Signaling (offer/answer/ICE) rides on the existing Colyseus room channel.
 *  - The "older" sessionId in each pair is the offerer; avoids glare.
 *  - Per-peer audio volume is set by the scene each frame based on distance
 *    and which area each player is in (rooms = private, lounge = proximity).
 *
 * Mesh design caps practical use at ~8 players. We have 6 — well under.
 *
 * Browser permission flow: getUserMedia is called from `start()`; on rejection
 * the manager runs in "listener-only" mode (no outgoing track, but still
 * receives others' audio).
 */

export interface VoicePeer {
  sessionId: string;
  pc: RTCPeerConnection;
  audioEl: HTMLAudioElement;
  hasRemoteStream: boolean;
  /** ms timestamp when the connection first dropped; used to debounce restarts. */
  failedSince: number | null;
  /** True while an ICE restart is in flight (avoid stacking). */
  restartingIce: boolean;
}

export type VolumeResolver = (sessionId: string) => number; // 0..1

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

export class VoiceManager {
  private room: Room<any>;
  private mySessionId: string;
  private localStream: MediaStream | null = null;
  private peers = new Map<string, VoicePeer>();
  private volumeResolver: VolumeResolver;
  private muted = false;

  private healthCheckTimer: number | null = null;
  private audioUnlocked = false;

  constructor(room: Room<any>, mySessionId: string, volumeResolver: VolumeResolver) {
    this.room = room;
    this.mySessionId = mySessionId;
    this.volumeResolver = volumeResolver;

    this.room.onMessage("voice-signal", (msg: { from: string; signal: any }) => {
      this.handleSignal(msg.from, msg.signal).catch(console.error);
    });

    // Browser autoplay policy: <audio> can't autoplay before a user gesture.
    // We attempt play() in createPeer, but if the browser blocks it, register
    // a one-shot unlock listener so the next click/keypress kicks it loose.
    const unlock = () => {
      this.audioUnlocked = true;
      for (const p of this.peers.values()) {
        p.audioEl.play().catch(() => {});
      }
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });

    // Periodic health check — recovers peers that rotted past the
    // onconnectionstatechange callback (e.g. browsers that don't fire the
    // event reliably, or when the offerer side never noticed).
    this.healthCheckTimer = window.setInterval(() => this.checkPeerHealth(), 8_000);
  }

  async start(): Promise<{ ok: boolean; reason?: string }> {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      console.log("[voice] microphone acquired");
      return { ok: true };
    } catch (err) {
      console.warn("[voice] mic permission denied — running in listen-only mode:", err);
      return { ok: false, reason: String(err) };
    }
  }

  /**
   * Decide whether THIS client should be the offerer when forming a peer
   * with `otherSessionId`. Rule: lexicographically smaller sessionId offers.
   * Symmetric so each pair only initiates once.
   */
  private isOfferer(otherSessionId: string): boolean {
    return this.mySessionId < otherSessionId;
  }

  /** Called by the scene when a remote player joins or leaves. */
  async onRemotePlayerAdded(sessionId: string) {
    if (sessionId === this.mySessionId) return;
    if (this.peers.has(sessionId)) return;

    const peer = this.createPeer(sessionId);
    this.peers.set(sessionId, peer);

    if (this.isOfferer(sessionId)) {
      await this.sendOffer(peer);
    }
  }

  onRemotePlayerRemoved(sessionId: string) {
    const peer = this.peers.get(sessionId);
    if (!peer) return;
    peer.pc.close();
    peer.audioEl.srcObject = null;
    peer.audioEl.remove();
    this.peers.delete(sessionId);
  }

  /** Called every frame by the scene to apply spatial volume. */
  applyVolumes() {
    for (const peer of this.peers.values()) {
      if (!peer.hasRemoteStream) continue;
      const v = Math.max(0, Math.min(1, this.volumeResolver(peer.sessionId)));
      peer.audioEl.volume = v;
    }
  }

  toggleMute(): boolean {
    if (!this.localStream) return false;
    this.muted = !this.muted;
    for (const t of this.localStream.getAudioTracks()) {
      t.enabled = !this.muted;
    }
    return this.muted;
  }

  isMuted() {
    return this.muted;
  }

  hasMic() {
    return !!this.localStream;
  }

  // —————————————————————————————— internals ——————————————————————————————

  private createPeer(sessionId: string): VoicePeer {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    // Add our local mic track (if we have one)
    if (this.localStream) {
      for (const track of this.localStream.getAudioTracks()) {
        pc.addTrack(track, this.localStream);
      }
    }

    // Hidden audio element to play remote stream
    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.controls = false;
    audioEl.style.display = "none";
    (audioEl as any).playsInline = true; // iOS Safari hint
    document.body.appendChild(audioEl);

    const peer: VoicePeer = {
      sessionId, pc, audioEl,
      hasRemoteStream: false,
      failedSince: null,
      restartingIce: false,
    };

    pc.ontrack = (ev) => {
      const stream = ev.streams[0];
      if (stream && audioEl.srcObject !== stream) {
        audioEl.srcObject = stream;
        peer.hasRemoteStream = true;
        audioEl.volume = 0; // start silent until volume resolver kicks in
        // Try to start playback immediately; if blocked by autoplay policy,
        // the unlock listener in constructor() will retry on first user gesture.
        audioEl.play().catch((e) => {
          console.warn(`[voice] autoplay blocked for ${sessionId}; will retry on user gesture`, e?.name);
        });
      }
    };

    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.sendSignal(sessionId, { type: "ice", candidate: ev.candidate });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[voice] ${sessionId} ice=${pc.iceConnectionState}`);
    };

    // RTCPeerConnection.connectionState is the high-level signal we care about
    // ("connected" / "disconnected" / "failed"). On failure or sustained
    // disconnection we trigger an ICE restart from the offerer side.
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      console.log(`[voice] ${sessionId} pc=${s}`);
      if (s === "failed" || s === "disconnected") {
        if (peer.failedSince === null) peer.failedSince = Date.now();
        // Schedule a restart — but debounced. Disconnections can self-heal.
        window.setTimeout(() => this.maybeRestartIce(peer), 4_000);
      } else if (s === "connected") {
        peer.failedSince = null;
        peer.restartingIce = false;
      } else if (s === "closed") {
        // Peer is dead; the health-check / room state cleanup will rebuild.
      }
    };

    return peer;
  }

  /** ICE restart on the offerer side; answerer side just receives the new offer. */
  private async maybeRestartIce(peer: VoicePeer) {
    if (peer.restartingIce) return;
    if (peer.pc.connectionState === "connected") return;
    if (peer.failedSince === null) return;
    if (Date.now() - peer.failedSince < 3_500) return; // still in grace window
    if (!this.isOfferer(peer.sessionId)) return;        // answerer waits for offer

    peer.restartingIce = true;
    try {
      console.log(`[voice] ICE restart -> ${peer.sessionId}`);
      const offer = await peer.pc.createOffer({ iceRestart: true });
      await peer.pc.setLocalDescription(offer);
      this.sendSignal(peer.sessionId, { type: "offer", sdp: peer.pc.localDescription });
    } catch (e) {
      console.error("[voice] ICE restart failed:", e);
      peer.restartingIce = false;
    }
  }

  /**
   * Periodic sweep — for each peer we check if the underlying connection has
   * been failed/disconnected for >12s and (a) trigger a restart if we're the
   * offerer, or (b) tear down + rebuild the peer entirely if a restart already
   * failed. This catches edge cases where state events didn't fire.
   */
  private checkPeerHealth() {
    const now = Date.now();
    for (const peer of this.peers.values()) {
      const s = peer.pc.connectionState;
      if (s === "failed" || s === "disconnected") {
        if (peer.failedSince === null) peer.failedSince = now;
        const downFor = now - peer.failedSince;
        if (downFor > 4_000 && !peer.restartingIce) {
          this.maybeRestartIce(peer);
        }
        if (downFor > 20_000) {
          // Restart attempts didn't help — full rebuild.
          console.warn(`[voice] hard rebuild peer ${peer.sessionId} (down ${downFor}ms)`);
          this.rebuildPeer(peer.sessionId);
        }
      } else if (s === "connected") {
        peer.failedSince = null;
      }
    }
  }

  /** Tear down and recreate a peer from scratch. Used when ICE restart can't recover. */
  private rebuildPeer(sessionId: string) {
    const old = this.peers.get(sessionId);
    if (old) {
      try { old.pc.close(); } catch {}
      old.audioEl.srcObject = null;
      old.audioEl.remove();
      this.peers.delete(sessionId);
    }
    const fresh = this.createPeer(sessionId);
    this.peers.set(sessionId, fresh);
    if (this.isOfferer(sessionId)) {
      this.sendOffer(fresh).catch((e) => console.error("[voice] rebuild offer failed:", e));
    }
  }

  private async sendOffer(peer: VoicePeer) {
    const offer = await peer.pc.createOffer({ offerToReceiveAudio: true });
    await peer.pc.setLocalDescription(offer);
    this.sendSignal(peer.sessionId, { type: "offer", sdp: peer.pc.localDescription });
  }

  private sendSignal(toSessionId: string, signal: any) {
    this.room.send("voice-signal", { to: toSessionId, signal });
  }

  private async handleSignal(fromSessionId: string, signal: any) {
    let peer = this.peers.get(fromSessionId);
    if (!peer) {
      // Unsolicited signal — could be the offerer reaching out before our
      // onRemotePlayerAdded fired. Spin up the peer now.
      peer = this.createPeer(fromSessionId);
      this.peers.set(fromSessionId, peer);
    }

    if (signal.type === "offer") {
      await peer.pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
      const answer = await peer.pc.createAnswer();
      await peer.pc.setLocalDescription(answer);
      this.sendSignal(fromSessionId, { type: "answer", sdp: peer.pc.localDescription });
    } else if (signal.type === "answer") {
      await peer.pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
    } else if (signal.type === "ice" && signal.candidate) {
      try {
        await peer.pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
      } catch (e) {
        console.warn("[voice] addIceCandidate failed:", e);
      }
    }
  }
}
